import { useState, useEffect } from 'react';
import {
  Modal,
  Box,
  TextField,
  Button,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tabs,
  Tab,
  Chip,
  IconButton,
  useMediaQuery,
  useTheme,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import { updateEmployee, deleteEmployee } from '../api/github';
import { hashPassword } from '../utils/auth';
import DeleteIcon from '@mui/icons-material/Delete';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AddIcon from '@mui/icons-material/Add';

const daysOfWeek = [
  { id: 'mon', label: 'Душанба' },
  { id: 'tue', label: 'Сешанба' },
  { id: 'wed', label: 'Чоршанба' },
  { id: 'thu', label: 'Пайшанба' },
  { id: 'fri', label: 'Жума' },
  { id: 'sat', label: 'Шанба' },
  { id: 'sun', label: 'Якшанба' }
];

export default function EditEmployeeModal({ open, onClose, employee, refreshList }) {
  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    const digits = phone.replace(/\D/g, '');
    let formatted = '+998';
    if (digits.length > 3) {
      formatted += ' ' + digits.substring(3, 5);
    }
    if (digits.length > 5) {
      formatted += ' ' + digits.substring(5, 8);
    }
    if (digits.length > 8) {
      formatted += ' ' + digits.substring(8, 10);
    }
    if (digits.length > 10) {
      formatted += ' ' + digits.substring(10, 12);
    }
    return formatted;
  };

  const [formData, setFormData] = useState({
    ...employee,
    lastName: employee.name.split(' ')[0] || '',
    firstName: employee.name.split(' ')[1] || '',
    middleName: employee.name.split(' ')[2] || '',
    phones: employee.phones ? employee.phones.map(formatPhoneNumber) : [formatPhoneNumber(employee.phone || '')],
    newPassword: '',
    schedule: employee.schedule || {
      workDays: ['mon', 'tue', 'wed', 'thu', 'fri'],
      restDays: ['sat', 'sun'],
      dailyHours: {
        start: '09:00',
        end: '18:00',
        breakStart: '13:00',
        breakEnd: '14:00'
      }
    },
    attendance: employee.attendance || [],
    other: employee.other || ''
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [activeTab, setActiveTab] = useState(0);
  const [isFormValid, setIsFormValid] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    const isValid =
      formData.lastName &&
      formData.lastName.trim().length >= 2 &&
      formData.firstName &&
      formData.firstName.trim().length >= 2 &&
      formData.username &&
      formData.username.trim().length >= 3 &&
      formData.salary > 0 &&
      formData.phones.every(phone => phone.replace(/\D/g, '').length >= 12 || !phone);

    setIsFormValid(isValid);
  }, [formData]);

  const handleDayToggle = (day) => {
    const newWorkDays = formData.schedule.workDays.includes(day)
      ? formData.schedule.workDays.filter((d) => d !== day)
      : [...formData.schedule.workDays, day];

    setFormData({
      ...formData,
      schedule: {
        ...formData.schedule,
        workDays: newWorkDays,
        restDays: daysOfWeek
          .map((d) => d.id)
          .filter((id) => !newWorkDays.includes(id))
      }
    });
  };

  const handleTimeChange = (field, value) => {
    setFormData({
      ...formData,
      schedule: {
        ...formData.schedule,
        dailyHours: {
          ...formData.schedule.dailyHours,
          [field]: value
        }
      }
    });
  };

  const handleAttendanceChange = (date, status) => {
    const updatedAttendance = [...formData.attendance];
    const index = updatedAttendance.findIndex(record => record.date === date);
    if (index >= 0) {
      updatedAttendance[index] = { date, status };
    } else {
      updatedAttendance.push({ date, status });
    }
    updatedAttendance.sort((a, b) => new Date(b.date) - new Date(a.date));
    setFormData({ ...formData, attendance: updatedAttendance });
  };

  const handlePhoneChange = (index, value) => {
    let digits = value.replace(/[^\d+]/g, '');
    if (!digits.startsWith('+998')) {
      digits = '+998' + digits.replace(/^\+/, '');
    }
    digits = digits.substring(0, 13);
    let formatted = digits.substring(0, 4);
    if (digits.length > 4) {
      formatted += ' ' + digits.substring(4, 6);
    }
    if (digits.length > 6) {
      formatted += ' ' + digits.substring(6, 9);
    }
    if (digits.length > 9) {
      formatted += ' ' + digits.substring(9, 11);
    }
    if (digits.length > 11) {
      formatted += ' ' + digits.substring(11, 13);
    }
    const newPhones = [...formData.phones];
    newPhones[index] = formatted;
    setFormData({ ...formData, phones: newPhones });
  };

  const addPhoneField = () => {
    setFormData({ ...formData, phones: [...formData.phones, ''] });
  };

  const removePhoneField = (index) => {
    const newPhones = formData.phones.filter((_, i) => i !== index);
    setFormData({ ...formData, phones: newPhones });
  };

  const handleSubmit = async () => {
    try {
      const fullName = `${formData.lastName} ${formData.firstName} ${formData.middleName}`.trim();

      const updatedData = {
        ...formData,
        name: fullName,
        phones: formData.phones.map(phone => phone.replace(/\D/g, '').replaceAll('+', '')),
        password: formData.newPassword
          ? hashPassword(formData.newPassword)
          : employee.password,
        attendance: formData.attendance,
        other: formData.other || null
      };

      await updateEmployee(employee.id, updatedData);
      setTimeout(() => {
        refreshList();
        onClose();
      }, 1500);
    } catch (error) {
      alert(error.message);
    }
  };

  const handleDelete = async () => {
    await deleteEmployee(employee.id);
    refreshList();
    onClose();
  };

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90%',
            maxWidth: 600,
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            maxHeight: '90vh',
            overflowY: 'auto'
          }}
        >
          <Typography variant="h5" gutterBottom>
            Тахрирлаш: {employee.name}
          </Typography>

          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
          >
            <Tab label="Асосий" />
            <Tab label="Иш графиги" />
            <Tab label="Посещаемость" />
          </Tabs>

          {activeTab === 0 && (
            <Box sx={{ mt: 3 }}>
              <TextField
                label="Фамилия"
                fullWidth
                margin="normal"
                value={formData.lastName}
                onChange={(e) =>
                  setFormData({ ...formData, lastName: e.target.value })
                }
              />
              <TextField
                label="Имя"
                fullWidth
                margin="normal"
                value={formData.firstName}
                onChange={(e) =>
                  setFormData({ ...formData, firstName: e.target.value })
                }
              />
              <TextField
                label="Отчество"
                fullWidth
                margin="normal"
                value={formData.middleName}
                onChange={(e) =>
                  setFormData({ ...formData, middleName: e.target.value })
                }
              />
              {formData.phones.map((phone, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <TextField
                    label={`Телефон рақами ${index + 1}`}
                    fullWidth
                    margin="normal"
                    value={phone}
                    onChange={(e) => handlePhoneChange(index, e.target.value)}
                    placeholder="+998 __ _______"
                    inputProps={{
                      inputMode: 'numeric',
                      pattern: '[+][0-9]{3} [0-9]{2} [0-9]{3} [0-9]{10}'
                    }}
                  />
                  {formData.phones.length > 1 && (
                    <IconButton onClick={() => removePhoneField(index)}>
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Box>
              ))}
              <Button
                startIcon={<AddIcon />}
                onClick={addPhoneField}
                sx={{ mb: 2 }}
              >
                Номер қўшиш
              </Button>
              <TextField
                label="Логин"
                fullWidth
                margin="normal"
                value={formData.username}
                onChange={(e) =>
                  setFormData({ ...formData, username: e.target.value })
                }
              />
              <TextField
                label="Хозирги пароль"
                margin="normal"
                value={employee.password}
                disabled
                fullWidth
              />
              <TextField
                label="Янги пароль"
                type="password"
                margin="normal"
                placeholder="Озгартирмаш учун бўш қолдиринг"
                value={formData.newPassword}
                onChange={(e) =>
                  setFormData({ ...formData, newPassword: e.target.value })
                }
                fullWidth
              />
              <TextField
                label="Иш хаққи"
                type="number"
                fullWidth
                margin="normal"
                value={formData.salary}
                onChange={(e) =>
                  setFormData({ ...formData, salary: e.target.value })
                }
              />
              <TextField
                label="Қўшимча маълумотлар"
                fullWidth
                margin="normal"
                multiline
                rows={4}
                value={formData.other}
                onChange={(e) =>
                  setFormData({ ...formData, other: e.target.value })
                }
                helperText="Қўшимча маълумотлар (ихтиёрий, ходим +-лари)"
              />
            </Box>
          )}

          {activeTab === 1 && (
            <Box sx={{ mt: 3 }}>
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
                >
                  <CalendarTodayIcon sx={{ mr: 1 }} />
                  Иш вақти
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {daysOfWeek.map((day) => (
                    <Chip
                      key={day.id}
                      label={day.label}
                      onClick={() => handleDayToggle(day.id)}
                      color={
                        formData.schedule.workDays.includes(day.id)
                          ? 'primary'
                          : 'default'
                      }
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Box>

              <Box>
                <Typography
                  variant="subtitle1"
                  sx={{ display: 'flex', alignItems: 'center', mb: 2 }}
                >
                  <AccessTimeIcon sx={{ mr: 1 }} />
                  Иш соати
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: 2
                  }}
                >
                  <TextField
                    label="Иш бошланиши"
                    type="time"
                    fullWidth
                    value={formData.schedule.dailyHours.start}
                    onChange={(e) => handleTimeChange('start', e.target.value)}
                  />
                  <TextField
                    label="Иш тугаши"
                    type="time"
                    fullWidth
                    value={formData.schedule.dailyHours.end}
                    onChange={(e) => handleTimeChange('end', e.target.value)}
                  />
                  <TextField
                    label="Обед"
                    type="time"
                    fullWidth
                    value={formData.schedule.dailyHours.breakStart}
                    onChange={(e) =>
                      handleTimeChange('breakStart', e.target.value)
                    }
                  />
                  <TextField
                    label="Обед тугаши"
                    type="time"
                    fullWidth
                    value={formData.schedule.dailyHours.breakEnd}
                    onChange={(e) => handleTimeChange('breakEnd', e.target.value)}
                  />
                </Box>
              </Box>
            </Box>
          )}

          {activeTab === 2 && (
            <Box sx={{ mt: 3 }}>
              <Typography
                variant="subtitle1"
                sx={{ display: 'flex', alignItems: 'center', mb: 2 }}
              >
                <CalendarTodayIcon sx={{ mr: 1 }} />
                Посещаемость
              </Typography>
              <TextField
                label="Дата"
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                fullWidth
                margin="normal"
                InputLabelProps={{ shrink: true }}
              />
              <FormControl fullWidth margin="normal">
                <InputLabel>Статус</InputLabel>
                <Select
                  value={
                    formData.attendance.find(record => record.date === selectedDate)?.status || 'work'
                  }
                  onChange={(e) => handleAttendanceChange(selectedDate, e.target.value)}
                  label="Статус"
                >
                  <MenuItem value="work">Рабочий</MenuItem>
                  <MenuItem value="rest">Выходной</MenuItem>
                  <MenuItem value="absent">Не работал</MenuItem>
                </Select>
              </FormControl>
              <Box sx={{ mt: 2, maxHeight: '300px', overflowY: 'auto' }}>
                {formData.attendance.map((record, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      p: 1,
                      borderBottom: '1px solid rgba(0,0,0,0.12)'
                    }}
                  >
                    <Typography>{record.date}</Typography>
                    <Typography
                      color={
                        record.status === 'work' ? 'success.main' :
                        record.status === 'rest' ? 'info.main' : 'error.main'
                      }
                    >
                      {record.status === 'work' ? 'Рабочий' :
                       record.status === 'rest' ? 'Выходной' : 'Не работал'}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mt: 4,
              pt: 2,
              borderTop: '1px solid rgba(0,0,0,0.12)'
            }}
          >
            <Button
              onClick={() => setDeleteConfirmOpen(true)}
              color="error"
              startIcon={<DeleteIcon />}
              variant="outlined"
            >
              Ходимни ўчириш
            </Button>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: 1,
              mt: 1,
              alignItems: isMobile ? 'flex-start' : 'center'
            }}
          >
            <Button
              onClick={handleSubmit}
              fullWidth
              variant="contained"
              disabled={!isFormValid}
            >
              Сақлаш
            </Button>
            <Button onClick={onClose} fullWidth>
              Бекор қилиш
            </Button>
          </Box>
        </Box>
      </Modal>
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Ўчиришни тасдиқлаш</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {employee.name} ходимни ўчириб ташламоқчимисиз?
            <br />
            Бу амални қайтариб бўлмайди.
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>
            Бекор қилиш
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            autoFocus
          >
            Ўчириш
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}